import {
  invalidFIOText,
  notOnlySpacesText,
  requiredFromListText,
  requiredText,
  validateFIO,
  validateSpaces,
} from "@frontend/roz-sdk-core";
import { z } from "zod";

// Вспомогательная функция для валидации ФИО
const createFIOValidation = () =>
  z
    .string()
    .refine((value) => value.trim(), { message: requiredText })
    .refine((value) => validateFIO(value), { message: invalidFIOText })
    .refine((value) => validateSpaces(value), { message: notOnlySpacesText });

const addressSchema = z.object({
  historyValues: z.array(z.string()).optional().nullable(),
  area: z.string().optional().nullable(),
  areaFiasId: z.string().optional().nullable(),
  areaKladrId: z.string().optional().nullable(),
  areaType: z.string().optional().nullable(),
  areaTypeFull: z.string().optional().nullable(),
  areaWithType: z.string().optional().nullable(),
  block: z.string().optional().nullable(),
  blockType: z.string().optional().nullable(),
  blockTypeFull: z.string().optional().nullable(),
  capitalMarker: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  cityArea: z.string().optional().nullable(),
  cityDistrict: z.string().optional().nullable(),
  cityDistrictFiasId: z.string().optional().nullable(),
  cityDistrictKladrId: z.string().optional().nullable(),
  cityDistrictType: z.string().optional().nullable(),
  cityDistrictTypeFull: z.string().optional().nullable(),
  cityDistrictWithType: z.string().optional().nullable(),
  cityFiasId: z.string().optional().nullable(),
  cityKladrId: z.string().optional().nullable(),
  cityType: z.string().optional().nullable(),
  cityTypeFull: z.string().optional().nullable(),
  cityWithType: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
  countryIsoCode: z.string().optional().nullable(),
  entrance: z.string().optional().nullable(),
  federalDistrict: z.string().optional().nullable(),
  fiasCode: z.string().optional().nullable(),
  fiasId: z.string().optional().nullable(),
  fiasLevel: z.string().optional().nullable(),
  flat: z.string().optional().nullable(),
  flatArea: z.string().optional().nullable(),
  flatCadnum: z.string().optional().nullable(),
  flatFiasId: z.string().optional().nullable(),
  flatType: z.string().optional().nullable(),
  flatTypeFull: z.string().optional().nullable(),
  floor: z.string().optional().nullable(),
  geoLat: z.string().optional().nullable(),
  geoLon: z.string().optional().nullable(),
  geonameId: z.string().optional().nullable(),
  house: z.string().optional().nullable(),
  houseCadnum: z.string().optional().nullable(),
  houseFiasId: z.string().optional().nullable(),
  houseKladrId: z.string().optional().nullable(),
  houseType: z.string().optional().nullable(),
  houseTypeFull: z.string().optional().nullable(),
  kladrId: z.string().optional().nullable(),
  name: z.string().optional().nullable(),
  postalBox: z.string().optional().nullable(),
  postalCode: z.string().optional().nullable(),
  region: z.string().optional().nullable(),
  regionFiasId: z.string().optional().nullable(),
  regionIsoCode: z.string().optional().nullable(),
  regionKladrId: z.string().optional().nullable(),
  regionType: z.string().optional().nullable(),
  regionTypeFull: z.string().optional().nullable(),
  regionWithType: z.string().optional().nullable(),
  settlement: z.string().optional().nullable(),
  settlementFiasId: z.string().optional().nullable(),
  settlementKladrId: z.string().optional().nullable(),
  settlementType: z.string().optional().nullable(),
  settlementTypeFull: z.string().optional().nullable(),
  settlementWithType: z.string().optional().nullable(),
  street: z.string().optional().nullable(),
  streetFiasId: z.string().optional().nullable(),
  streetKladrId: z.string().optional().nullable(),
  streetType: z.string().optional().nullable(),
  streetTypeFull: z.string().optional().nullable(),
  streetWithType: z.string().optional().nullable(),
  timezone: z.string().optional().nullable(),
  unparsedParts: z.string().optional().nullable(),
  value: z.string().optional().nullable(),
});

export const personalDataFormSchema = z
  .object({
    lastName: createFIOValidation(),
    firstName: createFIOValidation(),
    middleName: z.string().optional(),
    noMiddleName: z.boolean().optional(),
    birthDate: z.date().optional(),
    address: addressSchema,
    agreement: z.boolean().refine((value) => value === true, {
      message: requiredText,
    }),
    agreementPolicy: z.boolean().refine((value) => value === true, {
      message: requiredText,
    }),
    promotionalMailings: z.boolean(),
  })
  .superRefine((data, context) => {
    if (Object.keys(data.address).length === 0) {
      context.addIssue({
        path: ["address"],
        code: z.ZodIssueCode.custom,
        message: requiredFromListText,
      });
    }

    if (!data.birthDate) {
      context.addIssue({
        path: ["birthDate"],
        code: z.ZodIssueCode.custom,
        message: requiredText,
      });
    }

    // Если чекбокс НЕ выбран, то middleName обязательно и должно пройти все проверки
    if (!data.noMiddleName) {
      const middleName = data.middleName ?? "";

      if (!middleName.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["middleName"],
          message: requiredText,
        });
      }

      if (!validateFIO(middleName)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["middleName"],
          message: invalidFIOText,
        });
      }

      if (!validateSpaces(middleName)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["middleName"],
          message: notOnlySpacesText,
        });
      }
    }
  });

export type PersonalDataFormSchema = z.infer<typeof personalDataFormSchema>;
